import React, {useState, useEffect, useRef} from 'react';
import {Loader} from 'lucide-react';
import SectionWrapper from "../../../components/Page/SectionWrapper";
import useSignupContext from "../../../context/SignupContext";
import LoaderFullScreen from "../../../components/Page/LoaderFullScreen";
import YourCart from "../../../components/Cart/YourCart";
import YourBottomCart from "../../../components/Cart/YourBottomCart";
import {deleteAddress} from "../../../services/localStorage.service";
import {useNavigate} from "react-router-dom";
import {Profile, PromoCode} from "../../../@types/signup";
import {Field, Form, Formik, FormikProps} from "formik";
import * as Yup from "yup";
import {getHolidays, Holiday, validatePhoneNumber, validatePromoCode} from "../../../api/signup.api";
import DatePickerField from "../../../components/Form/DatePicker/DatePickerField";
import {premiumFormControlClassName} from "../../../components/Form/formControlStyles";
import ButtonPrimary from "../../../components/Form/Button/ButtoPrimary";
import ButtonAlternative from "../../../components/Form/Button/ButtonAlternative";
import {toast} from "react-toastify";
import ButtonDanger from "../../../components/Form/Button/ButtonDanger";
import {Cart} from "../../../@types/cart";

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  connectionDate: string;
  hasCurrentIsp: string;
  currentIspName: string;
  currentIspAccount: string;
}

const ContactDetails: React.FC = () => {

  const defaultProfile = {
    account: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    connection: {
      connectionDate: '',
      hasCurrentIsp: '',
      currentIspName: '',
      currentIspAccount: '',
    }
  };

  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const {clearContext, cartItems, persistCart, profile, persistProfile, cachedPlans} = useSignupContext();

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [holidayDates, setHolidayDates] = useState<Date[]>([]);
  const [loadedHolidayYears, setLoadedHolidayYears] = useState<number[]>([]);
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoCodeAdded, setPromoCodeAdded] = useState<PromoCode | null>(null);
  const [connectionDate, setConnectionDate] = useState<string>('');
  const [hasCurrentIsp, setHasCurrentIsp] = useState<string>('');
  const [currentIspName, setCurrentIspName] = useState<string>('');
  const [currentIspAccount, setCurrentIspAccount] = useState<string>('');
  const [signupProfile, setSignupProfile] = useState<Profile | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState<boolean>(false);

  const parseHolidayDate = (value: string): Date | null => {
    const [day, month, year] = value.split('/').map(Number);

    if (!day || !month || !year) {
      return null;
    }

    return new Date(year, month - 1, day);
  };

  const loadHolidayYear = async (year: number) => {
    if (loadedHolidayYears.includes(year)) {
      return;
    }

    try {
      const holidays = await getHolidays(year);
      const parsedDates = holidays
        .map((holiday: Holiday) => parseHolidayDate(holiday.ObservedDate))
        .filter((date): date is Date => date !== null);

      setHolidayDates((currentDates) => {
        const mergedDates = [...currentDates];

        parsedDates.forEach((holidayDate) => {
          const exists = mergedDates.some((date) => date.toDateString() === holidayDate.toDateString());

          if (!exists) {
            mergedDates.push(holidayDate);
          }
        });

        return mergedDates;
      });
      setLoadedHolidayYears((currentYears) => [...currentYears, year]);
    } catch (error) {
      console.error(`Unable to load holiday dates for ${year}`, error);
    }
  };

  const resetAddress = () => {
    // Clear context data
    clearContext();

    // Delete address cache
    deleteAddress();

    navigate('/address-select');
  };

  const validatePromo = async () => {
    if (promoCode === '') {
      toast.error("Enter a promo code", {
        position: "top-right",
        theme: "colored",
      });
      return;
    }

    setIsValidatingPromo(true);

    try {
      // Extract broadband plan from cart items
      const broadbandPlan = cartItems?.products?.find(product => product.type === 'PLAN');

      // Get the actual plan UUID from cached plans
      let broadbandPlanUuid: string | undefined;
      if (broadbandPlan && cachedPlans) {
        const plan = cachedPlans.plans.find(p => p.plan_id === broadbandPlan.productId);
        broadbandPlanUuid = plan?.uuid;
      }

      const response = await validatePromoCode(promoCode, broadbandPlanUuid);
      if (response.valid) {
        onPromoCodeAdded(response.promo);
      } else {
        toast.error(response.message || "Invalid promo code", {
          position: "top-right",
          theme: "colored",
        });
      }
    } catch (error: any) {
      // Extract error message from HTTP error response
      let errorMessage = "Unable to validate promo code";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        theme: "colored",
      });
    } finally {
      setIsValidatingPromo(false);
    }
  };

  useEffect(() => {
    void loadHolidayYear(new Date().getFullYear());
  }, []);

  const onPromoCodeAdded = (promo: PromoCode | null) => {
    setPromoCodeAdded(promo);
    if (cartItems == null) return;
    
    const tempCart: Cart = {...cartItems};
    if (tempCart != null) {
      // Remove existing promo items
      const productsWithoutPromo = [...(tempCart.products || [])].filter(p => p.type !== 'PROMO');

      if (promo) {
        // Add recurring discount item
        if (promo.recurring_inc_gst) {
          const recurringPromoItem = {
            productId: promo.promo_code,
            productName: `PROMO - ${promo.promo_code}`,
            type: 'PROMO' as const,
            cost: -parseFloat(String(promo.recurring_inc_gst)) * 100,
            costShipping: 0,
            months: 0,
            isMonthly: true,
            promoCode: promo
          };
          productsWithoutPromo.push(recurringPromoItem);
        }

        // Add one-off discount item
        if (promo.one_off_inc_gst) {
          const oneOffPromoItem = {
            productId: `${promo.promo_code}_ONEOFF`,
            productName: `PROMO - ${promo.promo_code} (One-off)`,
            type: 'PROMO' as const,
            cost: -parseFloat(String(promo.one_off_inc_gst)) * 100,
            costShipping: 0,
            months: 0,
            isMonthly: false,
            promoCode: promo
          };
          productsWithoutPromo.push(oneOffPromoItem);
        }
      }

      tempCart.products = productsWithoutPromo;
      persistCart(tempCart);
    }
  };

  useEffect(() => {
    if (cartItems == null) {
      navigate('/');
      return;
    }

    // Find promo item in products array
    const promoItem = cartItems.products?.find(p => p.type === 'PROMO');
    setPromoCodeAdded(promoItem?.promoCode || null);
    setSignupProfile(profile);
    if (profile) {
      setFirstName(profile.account.firstName);
      setLastName(profile.account.lastName);
      setEmail(profile.account.email);
      setPhone(profile.account.phone);
      setConnectionDate(profile.connection.connectionDate);
      setHasCurrentIsp(profile.connection.hasCurrentIsp);
      setCurrentIspName(profile.connection.currentIspName);
      setCurrentIspAccount(profile.connection.currentIspAccount);
    }
  }, []);

  useEffect(() => {
    const tempProfile = signupProfile ? signupProfile : defaultProfile;
    tempProfile.account.firstName = firstName;
    tempProfile.account.lastName = lastName;
    tempProfile.account.email = email;
    tempProfile.account.phone = phone;

    tempProfile.connection.connectionDate = connectionDate;
    tempProfile.connection.hasCurrentIsp = hasCurrentIsp;
    tempProfile.connection.currentIspName = currentIspName;
    tempProfile.connection.currentIspAccount = currentIspAccount;

    persistProfile(tempProfile);
  }, [firstName, lastName, email, phone, connectionDate, hasCurrentIsp, currentIspName, currentIspAccount]);

  useEffect(() => {
    setPromoCode(promoCodeAdded?.promo_code ?? '');
  }, [promoCodeAdded]);

  const SignupSchema = Yup.object().shape({
    firstName: Yup.string()
        .min(3, 'First name is too short!')
        .max(50, 'First name is too long!')
        .required('First name is required'),
    lastName: Yup.string()
        .min(3, 'Last name is too short!')
        .max(50, 'Last name is too long!')
        .required('Last name is required'),
    email: Yup.string().email('Invalid email').required('Email address is required'),
    phone:
        Yup.string()
            .required('Phone number is required')
            .test('checkPhoneNumber', 'Invalid phone number', async function (value) {
              setIsValidating(true);
              const results = await validatePhoneNumber(value);
              setIsValidating(false);

              return results.valid;
            }),
    connectionDate: Yup.string().trim().required('Connection date is required'),
    hasCurrentIsp: Yup.string().trim().required('Current provider is required'),
    currentIspName: Yup.string().when("hasCurrentIsp", {
      is: (val: string) => {
        return val === "Yes";
      },
      then: (s) => s.required('Provider name is required'),
      otherwise: (s) => s,
    }),
    currentIspAccount: Yup.string().when("hasCurrentIsp", {
      is: (val: string) => {
        return val === "Yes";
      },
      then: (s) => s.required('Account number name is required'),
      otherwise: (s) => s,
    }),
  });

  return (
      <div className='grow'>
        <div className="p-4 md:p-8 w-full">
          {(isValidating) && <LoaderFullScreen message="Validating your details..."/>}
          <section className='py-8 lg:py-4'>
            <SectionWrapper>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-fred-purple-500">Sign-Up for FREDNET Residential Broadband</h1>
              <div className='mt-2 sm:mt-4'>
                <p className='text-sm sm:text-lg md:text-xl font-light'>You are signing up for broadband at the address <span
                    className="font-medium">{cartItems?.address?.fullAddress}</span> - <span
                    className="font-bold underline cursor-pointer"
                    onClick={() => resetAddress()}>CHANGE</span></p>
              </div>
            </SectionWrapper>
          </section>
          {cartItems && (
              <section>
                <div className='grid lg:grid-cols-3 items-start mx-auto max-w-screen-2xl gap-8'>
                  <div className='lg:col-span-2 space-y-8'>
                    <Formik
                        innerRef={formikRef}
                        initialValues={{
                          firstName: firstName,
                          lastName: lastName,
                          email: email,
                          phone: phone,
                          connectionDate: connectionDate,
                          hasCurrentIsp: hasCurrentIsp,
                          currentIspName: currentIspName,
                          currentIspAccount: currentIspAccount,
                        }}
                        enableReinitialize={true}
                        validationSchema={SignupSchema}
                        validateOnChange={false} // Disable validation on change
                        validateOnBlur={false}   // Disable validation on blur
                        onSubmit={async (values) => {
                          await new Promise((r) => setTimeout(r, 500));
                          //alert(JSON.stringify(values, null, 2));
                          navigate('/summary');
                        }}
                    >
                      {({errors, touched, handleChange}) => (
                          <Form>
                            <div className="gap-4 grid grid-cols-2">
                              <div className='col-span-full mb-2'>
                                <h3 className="text-2xl font-medium text-fred-purple-500">Account Details</h3>
                              </div>
                              <div className="col-span-full sm:col-span-1">
                                <label htmlFor="firstName"
                                       className="block mb-2 text-base font-normal text-fred-purple-500">First
                                  Name *</label>
                                <Field name="firstName" value={firstName} disabled={isValidating}
                                       onChange={(e: any) => {
                                         handleChange(e);
                                         setFirstName(e.target.value);
                                       }}
                                       className={premiumFormControlClassName}/>
                                {errors.firstName && touched.firstName ? (
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.firstName}</p>
                                ) : null}
                              </div>
                              <div className="col-span-full sm:col-span-1">
                                <label htmlFor="lastName"
                                       className="block mb-2 text-base font-normal text-fred-purple-500">Last Name
                                  *</label>
                                <Field name="lastName" value={lastName} disabled={isValidating} onChange={(e: any) => {
                                  handleChange(e);
                                  setLastName(e.target.value);
                                }}
                                       className={premiumFormControlClassName}/>
                                {errors.lastName && touched.lastName ? (
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.lastName}</p>
                                ) : null}
                              </div>
                              <div className="col-span-full">
                                <label htmlFor="email"
                                       className="block mb-2 text-base font-normal text-fred-purple-500">Email *</label>
                                <Field name="email" type="email" value={email} disabled={isValidating}
                                       onChange={(e: any) => {
                                         handleChange(e);
                                         setEmail(e.target.value);
                                       }}
                                       className={premiumFormControlClassName}/>
                                {errors.email && touched.email ? (
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.email}</p>
                                ) : null}
                              </div>
                              <div className="col-span-full">
                                <label htmlFor="phone"
                                       className="block mb-2 text-base font-normal text-fred-purple-500">Mobile
                                  Number (without +64 or 0064) *</label>
                                <Field name="phone" value={phone} disabled={isValidating} onChange={(e: any) => {
                                  handleChange(e);
                                  setPhone(e.target.value);
                                }}
                                       className={premiumFormControlClassName}/>
                                {errors.phone && touched.phone ? (
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.phone}</p>
                                ) : null}
                              </div>
                            </div>
                            <div className="gap-4 grid grid-cols-2 mt-4">
                              <div className='col-span-full mb-2'>
                                <h3 className="text-2xl font-medium text-fred-purple-500">Connection Details</h3>
                              </div>
                              <div className="col-span-full md:col-span-1">
                                <label htmlFor="connectionDate"
                                       className="block mb-2 text-base font-normal text-fred-purple-500">Connection
                                  Date *</label>
                                <Field
                                    name="connectionDate"
                                    value={connectionDate}
                                    component={DatePickerField}
                                    excludedDates={holidayDates}
                                    onVisibleMonthChange={(date: Date) => {
                                      void loadHolidayYear(date.getFullYear());
                                    }}
                                    onDateChanged={(date: Date | null) => {
                                      if (date) {
                                        setConnectionDate(Intl.DateTimeFormat('en-CA', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit'
                                        }).format(date));
                                      }
                                    }}
                                />
                                {errors.connectionDate && touched.connectionDate ? (
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.connectionDate}</p>
                                ) : null}
                              </div>
                              <div className="col-span-full md:col-span-1">
                                <label htmlFor="hasCurrentIsp"
                                       className="block mb-2 text-base font-normal text-fred-purple-500">Do
                                  You Have a Current Broadband Provider? *</label>
                                <Field as="select" name="hasCurrentIsp" disabled={isValidating} onChange={(e: any) => {
                                  handleChange(e);
                                  setHasCurrentIsp(e.target.value);
                                }} value={hasCurrentIsp}
                                       className={premiumFormControlClassName}>
                                  <option key={1} value="" selected>Please Select</option>
                                  <option key={2} value="Yes">Yes</option>
                                  <option key={3} value="No">No</option>
                                </Field>
                                {errors.hasCurrentIsp && touched.hasCurrentIsp ? (
                                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.hasCurrentIsp}</p>
                                ) : null}
                              </div>
                              {hasCurrentIsp === 'Yes' && (
                                  <>
                                    <div className="col-span-full sm:col-span-1">
                                      <label htmlFor="currentIspName"
                                             className="block mb-2 text-base font-normal text-fred-purple-500">Provider's
                                        Name
                                        *</label>
                                      <Field name="currentIspName" value={currentIspName} disabled={isValidating}
                                             onChange={(e: any) => {
                                               handleChange(e);
                                               setCurrentIspName(e.target.value);
                                             }}
                                             className={premiumFormControlClassName}/>
                                      {errors.currentIspName && touched.currentIspName ? (
                                          <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.currentIspName}</p>
                                      ) : null}
                                    </div>
                                    <div className="col-span-full sm:col-span-1">
                                      <label htmlFor="currentIspAccount"
                                             className="block mb-2 text-base font-normal text-fred-purple-500">Account
                                        Number
                                        *</label>
                                      <Field name="currentIspAccount" value={currentIspAccount} disabled={isValidating}
                                             onChange={(e: any) => {
                                               handleChange(e);
                                               setCurrentIspAccount(e.target.value);
                                             }}
                                             className={premiumFormControlClassName}/>
                                      {errors.currentIspAccount && touched.currentIspAccount ? (
                                          <p className="mt-2 text-sm text-red-600 dark:text-red-500">{errors.currentIspAccount}</p>
                                      ) : null}
                                    </div>
                                  </>
                              )}
                            </div>
                          </Form>
                      )}
                    </Formik>
                    <div className="gap-4 grid grid-cols-2 mt-4">
                      <div className='col-span-full mb-2'>
                        <h3 className="text-2xl font-medium text-fred-purple-500">Promo Code</h3>
                      </div>
                      <div className="col-span-full md:col-span-1 space-y-2">
                        <label htmlFor="connectionDate"
                               className="block text-base font-normal text-fred-purple-500">Add Promo Code</label>
                        <input
                            type="text"
                            value={promoCode}
                            disabled={isValidatingPromo || promoCodeAdded !== null}
                            onChange={(e: any) => setPromoCode(e.target.value)}
                            placeholder="Enter promo code"
                            className={premiumFormControlClassName}
                        />
                      </div>
                      <div className="col-span-full md:col-span-1 space-y-2">
                        <label htmlFor="connectionDate"
                               className="hidden md:block text-base font-normal text-fred-purple-500">&nbsp;</label>
                        <div className="w-full flex gap-x-4">
                          <button
                            type="button"
                            disabled={isValidatingPromo || promoCodeAdded !== null}
                            onClick={() => validatePromo()}
                            className="w-full text-white bg-fred-blue-500 hover:bg-fred-purple-500 font-semibold rounded-lg text-base px-4 py-4 text-center inline-flex items-center justify-center cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                        >
                          {isValidatingPromo ? (
                            <>
                              <Loader className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400" />
                              Validating...
                            </>
                          ) : (
                            "Validate"
                          )}
                        </button>
                          <ButtonDanger text="Remove" disabled={isValidatingPromo || promoCodeAdded === null}
                                        onClick={() => {
                                          setPromoCodeAdded(null);
                                          setPromoCode('');
                                          onPromoCodeAdded(null);
                                        }}/>
                        </div>
                      </div>
                      {promoCodeAdded && (
                          <div className='col-span-full'>
                            <div
                                className="w-full flex flex-col p-4 text-green-800 border border-green-300 rounded-lg bg-green-50 space-y-2"
                                role="alert">
                              <h4 className="font-semibold text-base">{promoCodeAdded.promo_code} - Valid Promo Code!</h4>
                              <p className="text-fred-purple-500 italic">{promoCodeAdded.external_description}</p>
                              <p className="text-fred-purple-500">Your promo code is validated and added to your
                                broadband signup.</p>
                            </div>
                          </div>
                      )}
                    </div>
                    <div className="w-full space-y-2">
                      <ButtonAlternative text="Change My Plan" disabled={isValidating}
                                         onClick={() => navigate('/address-select')}/>
                      <ButtonPrimary text="Continue" disabled={isValidating}
                                     onClick={() => formikRef.current?.submitForm()}/>
                    </div>
                  </div>
                  <YourCart cart={cartItems} buttonText={'Continue'} isLoading={isValidating} hideButton
                            onChangeAddress={() => resetAddress()}/>
                </div>
              </section>
          )}
        </div>
        <div className="sticky bottom-0 z-50 w-full flex lg:hidden">
          {cartItems && (
              <YourBottomCart cart={cartItems} buttonText={'Continue'} isLoading={isValidating}
                              onSignup={() => formikRef.current?.submitForm()} onChangeAddress={() => resetAddress()}/>
          )}
        </div>
      </div>
  );
};

export default ContactDetails;
